import type { PaletteEntry } from '../model/format.ts';
import { matrixFromFlags } from '../model/format.ts';
import type { Xform } from '../model/xform.ts';
import { gameData, paletteSprite, readyImage, sortKey, textureVersion, type SpriteRef } from '../data/gameData.ts';
import { spriteWorldSize, type Drawable, type SpriteDrawable } from '../data/prefabInstance.ts';

export interface Camera {
  x: number;
  y: number;
  /** screen pixels per world unit */
  zoom: number;
}

export type RGBA = [number, number, number, number];

const tintCache = new Map<string, HTMLCanvasElement>();

/** Texture multiplied by a colour (Unity sprite/tilemap tint). Alpha is applied at draw time. */
export function tintedImage(tex: string, color: RGBA): CanvasImageSource | null {
  const img = readyImage(gameData().images.get(tex));
  if (!img) return null;
  if (color[0] >= 0.999 && color[1] >= 0.999 && color[2] >= 0.999) return img;
  // canvases (custom tile sheets) change while drawing
  const ver = img instanceof HTMLCanvasElement ? `@${textureVersion()}` : '';
  const key = `${tex}${ver}|${color[0].toFixed(3)},${color[1].toFixed(3)},${color[2].toFixed(3)}`;
  let c = tintCache.get(key);
  if (!c) {
    if (ver) for (const k of tintCache.keys()) if (k.startsWith(`${tex}@`)) tintCache.delete(k);
    c = document.createElement('canvas');
    c.width = img.width;
    c.height = img.height;
    const g = c.getContext('2d')!;
    g.drawImage(img, 0, 0);
    g.globalCompositeOperation = 'multiply';
    g.fillStyle = `rgb(${color[0] * 255},${color[1] * 255},${color[2] * 255})`;
    g.fillRect(0, 0, c.width, c.height);
    g.globalCompositeOperation = 'destination-in';
    g.drawImage(img, 0, 0);
    tintCache.set(key, c);
  }
  return c;
}

/** Set the canvas transform so that drawing in world units maps to the screen (y up). */
export function applyCamera(ctx: CanvasRenderingContext2D, cam: Camera, w: number, h: number) {
  ctx.setTransform(cam.zoom, 0, 0, -cam.zoom, w / 2 - cam.x * cam.zoom, h / 2 + cam.y * cam.zoom);
}

export function screenToWorld(cam: Camera, w: number, h: number, sx: number, sy: number): [number, number] {
  return [(sx - w / 2) / cam.zoom + cam.x, -(sy - h / 2) / cam.zoom + cam.y];
}

export function worldToScreen(cam: Camera, w: number, h: number, x: number, y: number): [number, number] {
  return [(x - cam.x) * cam.zoom + w / 2, -(y - cam.y) * cam.zoom + h / 2];
}

/**
 * Draw a sprite in local space (current ctx transform = object transform, y up).
 * Rect: pivot-relative in world units.
 */
export function drawSpriteLocal(ctx: CanvasRenderingContext2D, s: SpriteRef, color: RGBA, flipX = false, flipY = false) {
  const img = tintedImage(s.tex, color);
  if (!img) return;
  const ppu = s.texDef.ppu;
  const [rx, ry, rw, rh] = s.def.r;
  const w = rw / ppu, h = rh / ppu;
  const x0 = -s.def.p[0] * w, y0 = -s.def.p[1] * h;
  ctx.save();
  if (flipX || flipY) ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
  ctx.scale(1, -1);
  if (color[3] < 1) ctx.globalAlpha *= color[3];
  ctx.drawImage(img, rx, s.texDef.h - ry - rh, rw, rh, x0, -(y0 + h), w, h);
  ctx.restore();
}

function drawSpriteDrawable(ctx: CanvasRenderingContext2D, d: SpriteDrawable) {
  ctx.save();
  const m = d.world;
  ctx.transform(m.a, m.b, m.c, m.d, m.tx, m.ty);
  if (!d.drawMode) {
    drawSpriteLocal(ctx, d.sprite, d.color, d.flipX, d.flipY);
  } else {
    drawSizedSprite(ctx, d);
  }
  ctx.restore();
}

/** One run of a tiled axis: destination [d0, d0 + len) shows source fraction [f0, f1) of the tile. */
interface TileSpan {
  d0: number;
  len: number;
  f0: number;
  f1: number;
}

/**
 * Tiles of natural length `t` covering `len` world units. Continuous tiling is centred on the
 * region (partial tiles split evenly between the two ends); adaptive tiling stretches whole tiles
 * and adds one once the stretch passes the threshold.
 */
export function tileSpans(len: number, t: number, adaptive: boolean, threshold: number): TileSpan[] {
  if (len <= 1e-6) return [];
  if (t <= 1e-6) return [{ d0: 0, len, f0: 0, f1: 1 }];
  if (adaptive) {
    const ratio = len / t;
    let n = Math.floor(ratio + 1e-4);
    if (ratio - n > threshold + 1e-4 || n < 1) n++;
    const step = len / n;
    return Array.from({ length: n }, (_, k) => ({ d0: k * step, len: step, f0: 0, f1: 1 }));
  }
  const n = Math.max(1, Math.ceil(len / t - 1e-4));
  const start = (len - n * t) / 2;
  const out: TileSpan[] = [];
  for (let k = 0; k < n; k++) {
    const a = start + k * t;
    const ca = Math.max(a, 0), cb = Math.min(a + t, len);
    if (cb - ca > 1e-6) out.push({ d0: ca, len: cb - ca, f0: (ca - a) / t, f1: (cb - a) / t });
  }
  return out;
}

/**
 * SpriteRenderer drawMode 1 (sliced) and 2 (tiled). The sprite's border (9-slice) keeps its pixel
 * size and shrinks proportionally when the renderer is smaller than the borders. Sliced stretches
 * the edges and centre; tiled repeats the centre in both axes and the edges along their length.
 */
function drawSizedSprite(ctx: CanvasRenderingContext2D, d: SpriteDrawable) {
  const s = d.sprite;
  const img = tintedImage(s.tex, d.color);
  if (!img) return;
  const [W, H] = spriteWorldSize(d).map(Math.abs);
  if (W <= 1e-6 || H <= 1e-6) return;
  const ppu = s.texDef.ppu;
  const [rx, ry, rw, rh] = s.def.r;
  const [bl, bb, br, bt] = s.def.b ?? [0, 0, 0, 0];
  const fx = bl + br > 0 ? Math.min(1, (W * ppu) / (bl + br)) : 1;
  const fy = bb + bt > 0 ? Math.min(1, (H * ppu) / (bb + bt)) : 1;
  const xs = [0, (bl / ppu) * fx, W - (br / ppu) * fx, W], ys = [0, (bb / ppu) * fy, H - (bt / ppu) * fy, H];
  const pxs = [0, bl, rw - br, rw], pys = [0, bb, rh - bt, rh];
  const x0 = -s.def.p[0] * W, y0 = -s.def.p[1] * H;
  const top = s.texDef.h - ry - rh;
  const tiled = d.drawMode === 2;
  const adaptive = tiled && d.tileMode === 1;
  const threshold = d.tileThreshold ?? 0.5;
  ctx.save();
  if (d.flipX || d.flipY) ctx.scale(d.flipX ? -1 : 1, d.flipY ? -1 : 1);
  if (d.color[3] < 1) ctx.globalAlpha *= d.color[3];
  ctx.scale(1, -1);
  const whole: TileSpan[] = [{ d0: 0, len: 1, f0: 0, f1: 1 }];
  for (let i = 0; i < 3; i++) {
    const dw = xs[i + 1] - xs[i], sw = pxs[i + 1] - pxs[i];
    if (dw <= 1e-6 || sw <= 0) continue;
    const cols = tiled && i === 1 ? tileSpans(dw, sw / ppu, adaptive, threshold) : whole.map((c) => ({ ...c, len: dw }));
    for (let j = 0; j < 3; j++) {
      const dh = ys[j + 1] - ys[j], sh = pys[j + 1] - pys[j];
      if (dh <= 1e-6 || sh <= 0) continue;
      const rows = tiled && j === 1 ? tileSpans(dh, sh / ppu, adaptive, threshold) : whole.map((r) => ({ ...r, len: dh }));
      for (const c of cols) {
        for (const r of rows) {
          // source y runs down the image; destination y is flipped so it runs down too
          const sx = rx + pxs[i] + c.f0 * sw, sww = (c.f1 - c.f0) * sw;
          const sy = top + rh - pys[j] - r.f1 * sh, shh = (r.f1 - r.f0) * sh;
          ctx.drawImage(img, sx, sy, sww, shh, x0 + xs[i] + c.d0, -(y0 + ys[j] + r.d0 + r.len), c.len, r.len);
        }
      }
    }
  }
  ctx.restore();
}

// ---------- tile layers (chunk cached) ----------

const CHUNK = 32;
const MARGIN = 4;
const PX = 16;

interface Chunk {
  canvas: HTMLCanvasElement;
  cx: number;
  cy: number;
}

/**
 * Cached rendering of a sparse tile layer. Call `invalidate()` after changing tiles;
 * chunks are rebuilt lazily.
 */
export class TileLayerView {
  private chunks = new Map<string, Chunk>();
  private dirty = true;
  private byChunk = new Map<string, number[]>();
  private texVer = textureVersion();
  tiles: number[] = [];
  palette: PaletteEntry[] = [];
  /** animated tiles: frame overrides applied at draw time (play mode) */
  time = 0;

  set(tiles: number[], palette: PaletteEntry[]) {
    if (tiles === this.tiles && palette === this.palette) return;
    this.tiles = tiles;
    this.palette = palette;
    this.invalidate();
  }

  invalidate() {
    this.dirty = true;
    this.chunks.clear();
  }

  private index() {
    this.byChunk.clear();
    const t = this.tiles;
    for (let i = 0; i < t.length; i += 4) {
      if (t[i + 2] < 0) continue;
      const k = `${Math.floor(t[i] / CHUNK)},${Math.floor(t[i + 1] / CHUNK)}`;
      let a = this.byChunk.get(k);
      if (!a) this.byChunk.set(k, (a = []));
      a.push(t[i], t[i + 1], t[i + 2], t[i + 3]);
    }
    this.dirty = false;
  }

  private build(k: string): Chunk | null {
    const list = this.byChunk.get(k);
    if (!list) return null;
    const [cx, cy] = k.split(',').map(Number);
    const size = (CHUNK + MARGIN * 2) * PX;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const g = canvas.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    // chunk-local world transform: origin at (cx*CHUNK - MARGIN, cy*CHUNK - MARGIN), y up
    g.setTransform(PX, 0, 0, -PX, -(cx * CHUNK - MARGIN) * PX, size + (cy * CHUNK - MARGIN) * PX);
    for (let i = 0; i < list.length; i += 4) drawTile(g, this.palette, list[i], list[i + 1], list[i + 2], list[i + 3]);
    return { canvas, cx, cy };
  }

  draw(ctx: CanvasRenderingContext2D, view: [number, number, number, number], color: RGBA) {
    if (this.texVer !== textureVersion()) {
      this.texVer = textureVersion();
      this.chunks.clear();
    }
    if (this.dirty) this.index();
    const [vx0, vy0, vx1, vy1] = view;
    const c0x = Math.floor((vx0 - MARGIN) / CHUNK), c1x = Math.floor((vx1 + MARGIN) / CHUNK);
    const c0y = Math.floor((vy0 - MARGIN) / CHUNK), c1y = Math.floor((vy1 + MARGIN) / CHUNK);
    ctx.save();
    if (color[3] < 1) ctx.globalAlpha *= color[3];
    const tinted = color[0] < 0.999 || color[1] < 0.999 || color[2] < 0.999;
    for (const k of this.byChunk.keys()) {
      const [cx, cy] = k.split(',').map(Number);
      if (cx < c0x || cx > c1x || cy < c0y || cy > c1y) continue;
      let ch = this.chunks.get(k);
      if (!ch) {
        const b = this.build(k);
        if (!b) continue;
        ch = b;
        this.chunks.set(k, ch);
      }
      const size = CHUNK + MARGIN * 2;
      const src = tinted ? tintCanvas(ch.canvas, color, k) : ch.canvas;
      ctx.save();
      ctx.translate(cx * CHUNK - MARGIN, cy * CHUNK - MARGIN + size);
      ctx.scale(1, -1);
      ctx.drawImage(src, 0, 0, size, size);
      ctx.restore();
    }
    ctx.restore();
  }
}

const tintChunkCache = new WeakMap<HTMLCanvasElement, { key: string; canvas: HTMLCanvasElement }>();
function tintCanvas(src: HTMLCanvasElement, color: RGBA, _k: string): HTMLCanvasElement {
  const key = color.slice(0, 3).map((v) => v.toFixed(3)).join(',');
  const hit = tintChunkCache.get(src);
  if (hit && hit.key === key) return hit.canvas;
  const c = document.createElement('canvas');
  c.width = src.width;
  c.height = src.height;
  const g = c.getContext('2d')!;
  g.drawImage(src, 0, 0);
  g.globalCompositeOperation = 'multiply';
  g.fillStyle = `rgb(${color[0] * 255},${color[1] * 255},${color[2] * 255})`;
  g.fillRect(0, 0, c.width, c.height);
  g.globalCompositeOperation = 'destination-in';
  g.drawImage(src, 0, 0);
  tintChunkCache.set(src, { key, canvas: c });
  return c;
}

/** Draw one tile in world space (ctx transform maps world units, y up). */
export function drawTile(ctx: CanvasRenderingContext2D, palette: PaletteEntry[], x: number, y: number, p: number, f: number, color: RGBA = [1, 1, 1, 1]) {
  const s = paletteSprite(palette[p]);
  if (!s) return;
  ctx.save();
  ctx.translate(x + 0.5, y + 0.5);
  if (f) {
    const [a, b, c, d] = matrixFromFlags(f);
    // x' = a x + b y, y' = c x + d y  -> canvas transform(a, c, b, d)
    ctx.transform(a, c, b, d, 0, 0);
  }
  drawSpriteLocal(ctx, s, color);
  ctx.restore();
}

// ---------- scene drawing ----------

export interface SceneItem {
  key: number;
  draw: (ctx: CanvasRenderingContext2D) => void;
}

let maskScratch: HTMLCanvasElement | null = null;

/** Draw `draw`, then keep pixels inside (or outside) `mask`. Unity SpriteMask VisibleInsideMask / VisibleOutsideMask. */
export function clipToMask(ctx: CanvasRenderingContext2D, draw: (ctx: CanvasRenderingContext2D) => void, mask: (ctx: CanvasRenderingContext2D) => void, inside: boolean) {
  const src = ctx.canvas;
  const w = src.width, h = src.height;
  if (!w || !h) return;
  if (!maskScratch || maskScratch.width !== w || maskScratch.height !== h) {
    maskScratch = document.createElement('canvas');
    maskScratch.width = w;
    maskScratch.height = h;
  }
  const g = maskScratch.getContext('2d');
  if (!g) return;
  const m = ctx.getTransform();
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, w, h);
  g.setTransform(m.a, m.b, m.c, m.d, m.e, m.f);
  g.imageSmoothingEnabled = false;
  draw(g);
  g.globalCompositeOperation = inside ? 'destination-in' : 'destination-out';
  mask(g);
  g.globalCompositeOperation = 'source-over';
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.drawImage(maskScratch, 0, 0);
  ctx.restore();
}

function maskDrawable(d: SpriteDrawable): SpriteDrawable {
  return {
    kind: 'sprite',
    sprite: d.mask!.sprite,
    world: d.mask!.world,
    color: [1, 1, 1, 1],
    flipX: false,
    flipY: false,
    sortingLayer: d.sortingLayer,
    order: d.order,
    drawMode: 0,
    size: [1, 1],
    go: d.go,
  };
}

export function drawableItem(d: Drawable, view: [number, number, number, number], tileViews: Map<string, TileLayerView>, alpha = 1): SceneItem {
  if (d.kind === 'sprite') {
    const paint = (ctx: CanvasRenderingContext2D) => {
      if (alpha < 1) ctx.globalAlpha = alpha;
      drawSpriteDrawable(ctx, d);
      ctx.globalAlpha = 1;
    };
    const clipped = !!d.mask && (d.maskInteraction === 1 || d.maskInteraction === 2);
    return {
      key: sortKey(d.sortingLayer, d.order),
      draw: clipped ? (ctx) => clipToMask(ctx, paint, (g) => drawSpriteDrawable(g, maskDrawable(d)), d.maskInteraction === 1) : paint,
    };
  }
  let tv = tileViews.get(d.key);
  if (!tv) tileViews.set(d.key, (tv = new TileLayerView()));
  tv.set(d.tiles, d.palette);
  const v = tv;
  return {
    key: sortKey(d.sortingLayer, d.order),
    draw: (ctx) => {
      ctx.save();
      const m: Xform = d.world;
      ctx.transform(m.a, m.b, m.c, m.d, m.tx, m.ty);
      if (alpha < 1) ctx.globalAlpha = alpha;
      v.draw(ctx, localView(m, view), d.color);
      ctx.restore();
    },
  };
}

/** Convert a world view rect into the local space of a transform (axis-aligned bound). */
export function localView(m: Xform, view: [number, number, number, number]): [number, number, number, number] {
  if (m.a === 1 && m.b === 0 && m.c === 0 && m.d === 1) return [view[0] - m.tx, view[1] - m.ty, view[2] - m.tx, view[3] - m.ty];
  const det = m.a * m.d - m.b * m.c || 1;
  const ia = m.d / det, ib = -m.b / det, ic = -m.c / det, id = m.a / det;
  const pts = [
    [view[0], view[1]], [view[2], view[1]], [view[0], view[3]], [view[2], view[3]],
  ].map(([x, y]) => [ia * (x - m.tx) + ic * (y - m.ty), ib * (x - m.tx) + id * (y - m.ty)]);
  return [Math.min(...pts.map((p) => p[0])), Math.min(...pts.map((p) => p[1])), Math.max(...pts.map((p) => p[0])), Math.max(...pts.map((p) => p[1]))];
}

export function viewRect(cam: Camera, w: number, h: number): [number, number, number, number] {
  const hw = w / 2 / cam.zoom, hh = h / 2 / cam.zoom;
  return [cam.x - hw, cam.y - hh, cam.x + hw, cam.y + hh];
}

export function drawScene(ctx: CanvasRenderingContext2D, items: SceneItem[]) {
  items.sort((a, b) => a.key - b.key);
  for (const it of items) it.draw(ctx);
}
